import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { ContentDto, SocialVersionDto } from '@app/modules/content-ai/content-ai.types';
import { useContentAiGenerateSocialVersionContent } from '@mi-front/api-v2';
import { TextInput } from '@mi-front/ui';
import { Box, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useContent } from '../../../../hooks/useContent';
import { PrimaryButton } from '../../components';
import { isSocialOperationInProgress } from './is-social-operation-in-progress';

export function SocialVersionGenerate({
  content,
  version,
  allowInstructions,
  onCancel,
  onSuccess,
}: {
  content: ContentDto;
  version: SocialVersionDto;
  allowInstructions?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();
  const [instructions, setInstructions] = useState<string | undefined>();

  const query = useContent(content.id);
  const mutation = useContentAiGenerateSocialVersionContent({
    mutation: {
      onMutate: () => {
        queryClient.setQueryData(query.queryKey, {
          ...content,
          socialVersions: {
            versions: [
              ...content.socialVersions.versions.filter((v) => v.platform !== version.platform),
              {
                ...version,
                generation: {
                  status: 'IN_PROGRESS',
                },
              },
            ],
          },
        } as ContentDto);
      },
    },
  });

  const platform = socialPlatforms.get(version.platform);

  return (
    <Box sx={{ mb: 2 }}>
      {allowInstructions && (
        <Box sx={{ mb: 2 }}>
          <TextInput
            label={
              <FormattedMessage
                id="contentAi.instructionsOptional"
                defaultMessage="Instrucciones (opcional)"
              />
            }
            onChange={(evt) => {
              setInstructions(evt.target.value);
            }}
            autoFocus
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <PrimaryButton
          onClick={() => {
            mutation.mutate(
              {
                id: content.id,
                platform: version.platform,
                data: {
                  instructions,
                },
              },
              {
                onSuccess: async () => {
                  await queryClient.invalidateQueries({
                    queryKey: query.queryKey,
                  });
                  onSuccess && onSuccess();
                },
              },
            );
          }}
          disabled={query.data && isSocialOperationInProgress(query.data)}
          loading={mutation.isPending}
          startIcon={platform.Icon({ sx: { color: '#ffffff' } })}
          sx={{
            justifyContent: 'flex-start',
            width: '360px',
            '&:hover': { backgroundColor: '#9C3FCC' },
          }}
        >
          <FormattedMessage
            id="contentAi.socialVersion.generateFor"
            defaultMessage="Generar contenido para {platform}"
            values={{ platform: platform.name }}
          />
        </PrimaryButton>
        {onCancel && (
          <Button
            color="error"
            variant="outlined"
            onClick={onCancel}
            disabled={query.data && isSocialOperationInProgress(query.data)}
          >
            <FormattedMessage id="common.cancel" defaultMessage="Cancelar" />
          </Button>
        )}
      </Box>
    </Box>
  );
}
